import React from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import 'chart.js/auto';
import {
  ActionCount,
  ActionType,
  ALL,
  BreakdownFilterParams,
  BreakdownTab,
  DurationEnum,
  RoleActionPeriodBreakdown,
  RoleType,
  UserActionPeriodBreakdown,
} from '../../types/analyticTypes';
import { ApiInstance } from '../../api/api';
import BreakdownAnalyticsFilter from '../filters/BreakdownAnalyticsFilter';
import ActionBreakdownChart, { BreakdownGroup } from '../charts/ActionBreakdownChart';
import { useActionLogsPageQueryParams } from '../../hooks/useActionLogsPageQueryParams';
import { useRequiredSearchParams } from '../../hooks/useRequiredSearchParams';
import { getEnumParam, getEnumListParam, getStringListParam } from '../../utils/queryUtils';
import type { ActionLogUsersData } from '../../loaders/actionLogUsersLoader';
import { isEmailValidForRole } from '../filters/getUsersForRole';
import { parseTopN } from '../../utils/topN';
import pageStyles from './pageStyles';

/**
 * Aggregates the per-period `action_counts` arrays for a single group (role or user)
 * across every returned period, preserving the first period's action ordering for
 * downstream color-index stability.
 */
const sumActionCounts = (perPeriod: ActionCount[][]): ActionCount[] => {
  const totals = new Map<string, number>();
  const order: string[] = [];
  perPeriod.forEach((list) => {
    list.forEach((entry) => {
      if (!totals.has(entry.action)) {
        order.push(entry.action);
      }
      totals.set(entry.action, (totals.get(entry.action) ?? 0) + entry.action_count);
    });
  });
  return order.map((action) => ({ action, action_count: totals.get(action) ?? 0 }));
};

/**
 * Collapses the 12 monthly periods (year view) into one totals-per-role view.
 * The same set of roles is guaranteed to appear in every period, so summing
 * counts by role across periods is safe.
 */
const aggregateRolePeriods = (periods: RoleActionPeriodBreakdown[]): BreakdownGroup[] => {
  if (periods.length === 0) return [];

  const roleOrder: string[] = [];
  const perRolePeriodCounts = new Map<string, ActionCount[][]>();

  periods.forEach((period) => {
    period.roles.forEach((r) => {
      if (!perRolePeriodCounts.has(r.role)) {
        roleOrder.push(r.role);
        perRolePeriodCounts.set(r.role, []);
      }
      perRolePeriodCounts.get(r.role)!.push(r.action_counts);
    });
  });

  return roleOrder.map((roleName) => ({
    label: roleName,
    action_counts: sumActionCounts(perRolePeriodCounts.get(roleName) ?? []),
  }));
};

/**
 * Collapses the 12 monthly periods (year view) into one totals-per-user view.
 * The API guarantees the same top-N user set appears in every period (with
 * zero-filled counts for inactive months), so this aggregation preserves a
 * consistent ranking across the year.
 */
const aggregateUserPeriods = (periods: UserActionPeriodBreakdown[]): BreakdownGroup[] => {
  if (periods.length === 0) return [];

  const userOrder: string[] = [];
  const perUserPeriodCounts = new Map<string, ActionCount[][]>();

  periods.forEach((period) => {
    period.users.forEach((u) => {
      if (!perUserPeriodCounts.has(u.user_email)) {
        userOrder.push(u.user_email);
        perUserPeriodCounts.set(u.user_email, []);
      }
      perUserPeriodCounts.get(u.user_email)!.push(u.action_counts);
    });
  });

  return userOrder.map((email) => ({
    label: email,
    action_counts: sumActionCounts(perUserPeriodCounts.get(email) ?? []),
  }));
};

/** Role totals for a single period (used for the day/week/month views). */
const singlePeriodRoleGroups = (period: RoleActionPeriodBreakdown | undefined): BreakdownGroup[] =>
  (period?.roles ?? []).map((r) => ({ label: r.role, action_counts: r.action_counts }));

/** User totals for a single period (used for the day/week/month views). */
const singlePeriodUserGroups = (period: UserActionPeriodBreakdown | undefined): BreakdownGroup[] =>
  (period?.users ?? []).map((u) => ({ label: u.user_email, action_counts: u.action_counts }));

/**
 * Renders the "Actions by role & user" analytics tab: a duration/action/role/user
 * filter bar plus a stacked bar chart, with filter state synced to the URL so
 * links to a specific view can be shared and reloaded.
 */
function AnalyticsBreakdownPage() {
  const { userMap } = useLoaderData() as ActionLogUsersData;
  const { setParams } = useActionLogsPageQueryParams();
  const { params } = useRequiredSearchParams(['duration', 'actions', 'role', 'users', 'tab']);
  const [searchParams] = useSearchParams();

  const durationQueryParam = getEnumParam(DurationEnum, params.duration);
  const actionsQueryParam = getEnumListParam(ActionType, params.actions);
  const roleQueryParam = getEnumParam(RoleType, params.role);
  const usersQueryParam = getStringListParam(params.users);
  const tabQueryParam = getEnumParam(BreakdownTab, params.tab);
  const topNQueryParam = searchParams.get('top_n')?.trim() ?? undefined;

  const [duration, setDuration] = React.useState<DurationEnum>(
    durationQueryParam || DurationEnum.day
  );
  const [actionTypes, setActionTypes] = React.useState<ActionType[]>(
    actionsQueryParam || [ActionType.all]
  );
  const [role, setRole] = React.useState<RoleType>(roleQueryParam || RoleType.all);
  const [users, setUsers] = React.useState<string[]>(usersQueryParam || [ALL]);
  const [tab, setTab] = React.useState<BreakdownTab>(tabQueryParam || BreakdownTab.role);
  const [topN, setTopN] = React.useState<number>(parseTopN(topNQueryParam));

  const [isLoading, setIsLoading] = React.useState(false);
  const [apiErrorFlag, setApiErrorFlag] = React.useState(false);
  const [rolePeriods, setRolePeriods] = React.useState<RoleActionPeriodBreakdown[]>([]);
  const [userPeriods, setUserPeriods] = React.useState<UserActionPeriodBreakdown[]>([]);

  const [error, setError] = React.useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);

  // Guards against out-of-order responses: only the most recently issued
  // request is allowed to commit its result into state.
  const latestRequestRef = React.useRef(0);

  const buildParams = React.useCallback(
    (overrides: Partial<BreakdownFilterParams>) => {
      const nextDuration = overrides.duration ?? duration;
      const nextActions = overrides.actions ?? actionTypes;
      const nextRole = overrides.role ?? role;
      const nextUsers = overrides.users ?? users;
      const nextTab = overrides.tab ?? tab;
      const nextTopN = overrides.topN ?? topN;
      setParams({
        duration: nextDuration,
        actions: nextActions.join(','),
        role: nextRole,
        users: nextUsers.join(','),
        tab: nextTab,
        top_n: String(nextTopN),
      });
    },
    [duration, actionTypes, role, users, tab, topN, setParams]
  );

  const loadData = React.useCallback(
    async (
      activeTab: BreakdownTab,
      nextDuration: DurationEnum,
      nextActions: ActionType[],
      nextRole: RoleType,
      nextUsers: string[],
      nextTopN: number
    ) => {
      const requestId = ++latestRequestRef.current;
      const isStale = () => requestId !== latestRequestRef.current;

      setRolePeriods([]);
      setUserPeriods([]);
      setIsLoading(true);
      setApiErrorFlag(false);

      try {
        if (activeTab === BreakdownTab.role) {
          const response = await ApiInstance.getActionAnalyticsByRole(
            nextDuration,
            nextActions,
            nextRole,
            nextUsers
          );
          if (!isStale()) setRolePeriods(response.periods ?? []);
        } else {
          const response = await ApiInstance.getActionAnalyticsByUser(
            nextDuration,
            nextActions,
            nextRole,
            nextUsers,
            nextTopN
          );
          if (!isStale()) setUserPeriods(response.periods ?? []);
        }
      } catch {
        if (!isStale()) setApiErrorFlag(true);
      } finally {
        if (!isStale()) setIsLoading(false);
      }
    },
    []
  );

  const handleFilterChange = React.useCallback(
    (
      nextDuration: DurationEnum,
      nextActions: ActionType[],
      nextRole: RoleType,
      nextUsers: string[],
      nextTopN: number
    ) => {
      setDuration(nextDuration);
      setActionTypes(nextActions);
      setRole(nextRole);
      setUsers(nextUsers);
      setTopN(nextTopN);
      buildParams({
        duration: nextDuration,
        actions: nextActions,
        role: nextRole,
        users: nextUsers,
        topN: nextTopN,
      });
      loadData(tab, nextDuration, nextActions, nextRole, nextUsers, nextTopN);
    },
    [tab, buildParams, loadData]
  );

  const handleTabChange = React.useCallback(
    (_event: React.SyntheticEvent, nextTab: BreakdownTab) => {
      if (nextTab === tab) return;
      setTab(nextTab);
      buildParams({ tab: nextTab });
      loadData(nextTab, duration, actionTypes, role, users, topN);
    },
    [tab, duration, actionTypes, role, users, topN, buildParams, loadData]
  );

  React.useEffect(() => {
    if (
      durationQueryParam &&
      actionsQueryParam &&
      roleQueryParam &&
      usersQueryParam &&
      tabQueryParam &&
      isEmailValidForRole(userMap, role, users)
    ) {
      loadData(tab, duration, actionTypes, role, users, topN);
    } else {
      setError(
        'The link you used contains invalid filter options. Please select new values from the filter panel below.'
      );
      setOpenSnackbar(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isYear = duration === DurationEnum.year;

  const groups: BreakdownGroup[] = React.useMemo(() => {
    if (tab === BreakdownTab.role) {
      return isYear ? aggregateRolePeriods(rolePeriods) : singlePeriodRoleGroups(rolePeriods[0]);
    }
    return isYear ? aggregateUserPeriods(userPeriods) : singlePeriodUserGroups(userPeriods[0]);
  }, [tab, isYear, rolePeriods, userPeriods]);

  const rangeLabel = (() => {
    if (isYear) return 'the last 12 months';
    const singlePeriod =
      tab === BreakdownTab.role ? rolePeriods[0]?.period : userPeriods[0]?.period;
    return singlePeriod ?? `the selected ${duration}`;
  })();

  const chartTitle =
    tab === BreakdownTab.role
      ? `Actions by role — ${rangeLabel}`
      : `Actions by user — ${rangeLabel}`;

  const emptyMessage =
    tab === BreakdownTab.user
      ? 'No users have performed the selected actions in this range.'
      : 'No actions for the selected filters in this range.';

  return (
    <>
      <Typography sx={pageStyles.label} component="h1" variant="h4" align="center">
        <InfoOutlinedIcon fontSize="medium" color="primary" />
        Action analytics by role & user
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '10pt' }}>
        <Tabs value={tab} onChange={handleTabChange} aria-label="analytics breakdown tabs">
          <Tab label="By Role" value={BreakdownTab.role} />
          <Tab label="By User" value={BreakdownTab.user} />
        </Tabs>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10pt',
        }}
      >
        <BreakdownAnalyticsFilter
          option={duration}
          actions={actionTypes}
          role={role}
          users={users}
          topN={topN}
          activeTab={tab}
          userMap={userMap}
          onFilterChange={handleFilterChange}
        />
      </Box>

      <ActionBreakdownChart
        title={chartTitle}
        emptyMessage={emptyMessage}
        groups={groups}
        isLoading={isLoading}
        apiErrorFlag={apiErrorFlag}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={10000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AnalyticsBreakdownPage;
